// Phase 2b — Ask-PILLS FAB. Message list + input now come from Sendbird's
// `<GroupChannel>` (bound to the user's ai_assistant channel). Empty-state
// suggestions + complaint deep-link + open-full-page button remain.
// Anonymous users get a "Sign in to chat" CTA instead of a live channel.
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Sparkles, X, Stethoscope, Pill, FileText, Maximize2, AlertTriangle, LogIn } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/AuthContext";
import { useSendbird } from "@/hooks/useSendbird";
import SendbirdEmbeddedChannel from "@/components/sendbird/SendbirdEmbeddedChannel";

const SUGGESTIONS = [
    { icon: Stethoscope, text: "I have a headache, what can I take?" },
    { icon: Pill, text: "How does Paracetamol work?" },
    { icon: FileText, text: "Explain my latest prescription" },
];

const ChatFAB = () => {
    const { user } = useAuth();
    const { session, ready } = useSendbird();
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [pulsed, setPulsed] = useState(false);

    // Hide FAB on chat page itself, all admin/*, all business/*, and unauthenticated pages.
    const isHiddenPath =
        pathname === "/chat" ||
        pathname.startsWith("/chat/ai") ||
        pathname.startsWith("/admin") ||
        pathname.startsWith("/business") ||
        pathname === "/login" ||
        pathname === "/signup";

    useEffect(() => { if (open) setPulsed(true); }, [open]);
    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape" && open) setOpen(false); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open]);

    if (isHiddenPath) return null;

    // Deterministic AI channel URL for the current user
    const aiChannelUrl = session?.user_id ? `pills_ai_${session.user_id}` : null;

    return (
        <>
            <button
                onClick={() => setOpen(true)}
                className={`fixed bottom-6 right-6 z-[60] inline-flex h-14 items-center gap-2 rounded-full bg-gradient-to-br from-brand-700 via-brand-500 to-brand-400 px-5 text-white shadow-lg shadow-brand-700/30 transition-transform hover:scale-105 active:scale-95 ${!pulsed ? "animate-pulse-soft" : ""}`}
                aria-label="Ask PILLS AI assistant"
                data-testid="chat-fab-button"
            >
                <Sparkles className="h-5 w-5" />
                <span className="hidden font-semibold sm:inline">{t("Ask PILLS")}</span>
            </button>

            {open && (
                <div className="fixed inset-0 z-[70]" data-testid="chat-fab-panel">
                    <div className="absolute inset-0 bg-black/30 backdrop-blur-sm sm:bg-black/20" onClick={() => setOpen(false)} />

                    <aside className="absolute bottom-0 right-0 flex h-full w-full flex-col bg-white shadow-2xl sm:bottom-6 sm:right-6 sm:h-[640px] sm:w-[420px] sm:rounded-3xl sm:border sm:border-zinc-100">
                        <header className="flex items-center justify-between border-b border-zinc-100 px-5 py-4">
                            <div className="flex items-center gap-2">
                                <div className="grid h-9 w-9 place-items-center rounded-full bg-gradient-to-br from-brand-700 to-brand-400 text-white">
                                    <Sparkles className="h-4 w-4" />
                                </div>
                                <div>
                                    <p className="text-sm font-semibold text-zinc-900">{t("PILLS AI Assistant")}</p>
                                    <p className="text-[10px] text-zinc-500">{t("Health questions, drug info, refill help")}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-1">
                                {user && (
                                    <button
                                        onClick={() => { setOpen(false); navigate("/complaints/new"); }}
                                        className="rounded-full p-2 text-rose-500 hover:bg-rose-50"
                                        aria-label={t("Open complaint wizard")}
                                        title={t("Have a complaint?")}
                                        data-testid="chat-fab-complaint-cta"
                                    >
                                        <AlertTriangle className="h-4 w-4" />
                                    </button>
                                )}
                                <button
                                    onClick={() => { setOpen(false); navigate("/chat"); }}
                                    className="rounded-full p-2 text-zinc-400 hover:bg-zinc-100"
                                    aria-label="Open full chat"
                                    data-testid="sendbird-fab-open-full"
                                    title={t("Open full chat")}
                                >
                                    <Maximize2 className="h-4 w-4" />
                                </button>
                                <button onClick={() => setOpen(false)} className="rounded-full p-2 text-zinc-400 hover:bg-zinc-100" aria-label="Close" data-testid="chat-fab-close">
                                    <X className="h-4 w-4" />
                                </button>
                            </div>
                        </header>

                        {/* Body */}
                        <div className="min-h-0 flex-1 overflow-hidden" data-testid="sendbird-fab-embed">
                            {!user ? (
                                <div className="grid h-full place-items-center px-6 text-center">
                                    <div>
                                        <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-gradient-to-br from-brand-100 to-brand-50">
                                            <Sparkles className="h-6 w-6 text-brand-700" />
                                        </div>
                                        <h3 className="mt-3 text-base font-semibold tracking-tight text-zinc-900">
                                            {t("Sign in to chat with PILLS AI")}
                                        </h3>
                                        <p className="mt-1 text-xs text-zinc-500">
                                            {t("Free medical Q&A, powered by AI — with a doctor a tap away.")}
                                        </p>
                                        <button
                                            onClick={() => { setOpen(false); navigate("/login?redirect=/chat"); }}
                                            className="mt-5 inline-flex items-center gap-1.5 rounded-full bg-brand-700 px-5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brand-600"
                                            data-testid="chat-fab-login-cta"
                                        >
                                            <LogIn className="h-4 w-4" /> {t("Sign in")}
                                        </button>
                                        <ul className="mt-6 space-y-1 text-left text-xs text-zinc-500">
                                            {SUGGESTIONS.map((s, i) => (
                                                <li key={i} className="flex items-center gap-2">
                                                    <s.icon className="h-3.5 w-3.5 shrink-0 text-brand-500" />
                                                    <span>{s.text}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            ) : ready && aiChannelUrl ? (
                                <SendbirdEmbeddedChannel
                                    channelUrl={aiChannelUrl}
                                    customType="ai_assistant"
                                    height="100%"
                                    testId="sendbird-fab-channel"
                                    emptyMessage={t("Setting up your PILLS AI channel…")}
                                />
                            ) : (
                                <div className="grid h-full place-items-center text-sm text-zinc-500">
                                    <div className="animate-pulse">
                                        <Sparkles className="mx-auto h-6 w-6 text-brand-500" />
                                        <p className="mt-2">Connecting…</p>
                                    </div>
                                </div>
                            )}
                        </div>

                        <p className="border-t border-zinc-100 px-5 py-2 text-[10px] text-zinc-400">
                            {t("Not a substitute for medical advice. Always consult your doctor.")}
                        </p>
                    </aside>
                </div>
            )}
        </>
    );
};

export default ChatFAB;
